import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  ActivityIcon, 
  Clock, 
  User, 
  Search, 
  RefreshCw,
  Download,
  Eye,
  Package,
  Send,
  CheckCircle,
  XCircle,
  ArrowUp,
  ArrowDown,
  FileText,
  Users
} from 'lucide-react';
import { Link } from "wouter";
import { formatDistanceToNow } from 'date-fns';

interface Activity {
  id: string;
  type: string;
  description: string;
  created_at: string;
  createdAt?: string; // 兼容字段
  user_name?: string;
  userName?: string; // 兼容字段
  company_name?: string;
  companyName?: string; // 兼容字段
  package_id?: string;
  packageId?: string; // 兼容字段
  project_id?: string;
  projectId?: string; // 兼容字段
  package_number?: string;
  packageNumber?: string; // 兼容字段
  package_name?: string;
  packageName?: string; // 兼容字段
}

interface ActivityCardProps {
  activities: Activity[];
  isLoading?: boolean;
  onRefresh?: () => void;
}

const getActivityIcon = (type: string) => {
  const iconClass = "w-4 h-4";
  switch (type) {
    case 'package_created':
      return <Package className={`${iconClass} text-blue-500`} />;
    case 'package_submitted':
      return <Send className={`${iconClass} text-green-500`} />;
    case 'package_viewed':
      return <Eye className={`${iconClass} text-purple-500`} />;
    case 'package_download':
    case 'package_downloaded':
      return <Download className={`${iconClass} text-orange-500`} />;
    case 'tender_returned':
      return <ArrowUp className={`${iconClass} text-blue-500`} />;
    case 'tender_awarded':
    case 'package_awarded':
      return <CheckCircle className={`${iconClass} text-green-500`} />;
    case 'tender_rejected':
    case 'package_rejected':
      return <XCircle className={`${iconClass} text-red-500`} />;
    case 'return_submitted':
      return <FileText className={`${iconClass} text-blue-500`} />;
    case 'files_added':
      return <FileText className={`${iconClass} text-orange-500`} />;
    default:
      return <ActivityIcon className={`${iconClass} text-gray-500`} />;
  }
};

const getActivityBadgeColor = (type: string) => {
  switch (type) {
    case 'package_created':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'package_submitted':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'package_viewed':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'package_download':
    case 'package_downloaded':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'tender_returned':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'tender_awarded':
    case 'package_awarded':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'tender_rejected':
    case 'package_rejected':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'return_submitted':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'files_added':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const formatActivityType = (type: string) => {
  switch (type) {
    case 'package_created':
      return 'Package Created';
    case 'package_submitted':
      return 'Package Submitted';
    case 'package_viewed':
      return 'Package Viewed';
    case 'package_download':
    case 'package_downloaded':
      return 'Package Downloaded';
    case 'tender_returned':
    case 'return_submitted':
      return 'Package Returned';
    case 'tender_awarded':
    case 'package_awarded':
      return 'Package Awarded';
    case 'tender_rejected':
    case 'package_rejected':
      return 'Package Rejected';
    case 'files_added':
      return 'Files Added';
    default:
      return 'Activity';
  }
};

const formatTimeAgo = (dateString: string | Date | undefined) => {
  try {
    if (!dateString) {
      return 'Unknown time';
    }
    // Ensure we're working with a Date object
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Format with locale support
    return formatDistanceToNow(date, { 
      addSuffix: true,
      includeSeconds: true // Show "just now" for very recent activities
    });
  } catch (error) {
    console.error('Error formatting time:', error, dateString);
    return 'Unknown time';
  }
};

export default function ActivityCard({ activities = [], isLoading, onRefresh }: ActivityCardProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');

  const filteredActivities = useMemo(() => {
    let filtered = activities || [];

    // Deduplicate by id first to prevent duplicate entries
    const seenIds = new Set();
    filtered = filtered.filter(activity => {
      if (activity.id && !seenIds.has(activity.id)) {
        seenIds.add(activity.id);
        return true;
      }
      // If no id, use a combination of type, packageId, userEmail, and createdAt as unique key
      if (!activity.id) {
        const uniqueKey = `${activity.type}-${activity.packageId}-${activity.userEmail || activity.userName}-${activity.created_at || activity.createdAt}`;
        if (!seenIds.has(uniqueKey)) {
          seenIds.add(uniqueKey);
          return true;
        }
      }
      return false;
    });

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(activity => 
        activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.packageNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.packageName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by activity type
    if (activityFilter !== 'all') {
      if (activityFilter === 'tender_returned') {
        // Include both tender_returned and return_submitted for backward compatibility
        filtered = filtered.filter(activity => 
          activity.type === 'tender_returned' || activity.type === 'return_submitted'
        );
      } else if (activityFilter === 'package_download') {
        // Include both package_download and package_downloaded for backward compatibility
        filtered = filtered.filter(activity => 
          activity.type === 'package_download' || activity.type === 'package_downloaded'
        );
      } else if (activityFilter === 'award_reset') {
        // Include both award_reset and package_reset for backward compatibility
        filtered = filtered.filter(activity => 
          activity.type === 'award_reset' || activity.type === 'package_reset'
        );
      } else {
        filtered = filtered.filter(activity => activity.type === activityFilter);
      }
    }

    return filtered;
  }, [activities, searchTerm, activityFilter]);

  const activityTypes = [
    { value: 'all', label: 'All Activities' },
    { value: 'package_created', label: 'Package Created' },
    { value: 'package_submitted', label: 'Package Submitted' },
    { value: 'package_viewed', label: 'Package Viewed' },
    { value: 'package_download', label: 'Package Downloaded' }, // Filter value, matches both 'package_download' and 'package_downloaded'
    { value: 'tender_returned', label: 'Package Returned' },
    { value: 'package_awarded', label: 'Package Awarded' },
    { value: 'package_rejected', label: 'Package Rejected' },
    { value: 'award_reset', label: 'Package Reset' }
  ];

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center space-x-2">
          <ActivityIcon className="w-5 h-5" />
          <span>Recent Activity</span>
        </CardTitle>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            className="flex items-center space-x-1"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Recent Activity</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Filter by package number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={activityFilter} onValueChange={setActivityFilter}>
            <SelectTrigger>
              <SelectValue placeholder="All Activities" />
            </SelectTrigger>
            <SelectContent>
              {activityTypes.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ScrollArea className="h-[400px]">
          <div className="space-y-3">
          {!filteredActivities || filteredActivities.length === 0 ? (
            <div className="text-center py-8">
              <ActivityIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No recent activity</h3>
              <p className="text-gray-500">
                Activity will appear here when packages are created or submitted
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActivities.map((activity, index) => (
                <div key={`${activity.id}-${index}-${activity.created_at || activity.createdAt}`} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <Badge 
                        variant="outline" 
                        className={`text-xs font-medium ${getActivityBadgeColor(activity.type)}`}
                      >
                        {formatActivityType(activity.type)}
                      </Badge>
                      <span className="text-xs text-gray-500 flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTimeAgo(activity.created_at || activity.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 font-medium mb-1">
                      {activity.packageId ? (
                        <>
                          <span className="text-gray-900">
                            {activity.description.split(' package ')[0]} package{' '}
                          </span>
                          <Link 
                            href={`/tender-packages/projects/${activity.projectId}/${activity.packageId}`} 
                            className="text-blue-600 hover:text-blue-700 cursor-pointer underline"
                          >
                            {activity.packageNumber} - {activity.packageName}
                          </Link>
                        </>
                      ) : (
                        <span className="text-gray-900">
                          {activity.description}
                        </span>
                      )}
                    </p>
                    <div className="flex items-center text-xs text-gray-600">
                      <User className="w-3 h-3 mr-1" />
                      <span>{activity.company_name || activity.companyName || 'N/A'}</span>
                      <span className="mx-1">•</span>
                      <span className="text-gray-500">{activity.user_email || activity.userEmail || activity.user_name || activity.userName || 'Unknown User'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}